import type { FC } from 'react';
import { useCallback } from 'react';
import H3 from '@components/H3';
import { InlineEditableText } from '@components/InlineEditableText';
import InputText from '@components/InputText';
import type { UIInputValue } from '@components/types';
import {
  documentTitleClasses,
  EntityField,
  type OnSaveFn,
  useEditableField,
} from '@ContractBuilder/contract-builder-header';
import type { DbLibraryBlock } from '@domain/schemas/block.schema';
import { updateBlock } from '@mutations/updateBlock';
import { isClausesPath } from '@utils/app-paths';
import { pick } from 'lodash-es';

interface LibraryBlockEditableNameFieldProps {
  data: DbLibraryBlock;
  hasOperationsDisabled: boolean;
  onRefetch?: () => Promise<any> | void;
}

const CLAUSE_RELATED_KEYS: Array<keyof DbLibraryBlock> = ['clause_type', 'classification', 'clause_reference'];

export const LibraryBlockEditableNameField: FC<LibraryBlockEditableNameFieldProps> = ({
  data,
  hasOperationsDisabled,
  onRefetch,
}) => {
  const isClause = isClausesPath();

  const handleNameSave: OnSaveFn = useCallback(
    async (_field: string, value: string) => {
      const basePayload = isClause ? pick(data, CLAUSE_RELATED_KEYS) : {};

      await updateBlock(data.id, { ...basePayload, name: value });
      await onRefetch?.();
    },
    [data, isClause, onRefetch],
  );

  const { onCancel, onChange, onClick, onUpdate, state } = useEditableField({
    name: EntityField.Name,
    defaultValue: data.name,
    onSave: handleNameSave,
  });

  const placeholder = isClause ? 'Clause name' : 'Block name';

  return (
    <InlineEditableText
      editor={
        <InputText
          value={state.value as UIInputValue}
          onChange={(event) => onChange(event.target.value)}
          name="library-block-name"
          dataTestId="text:library-block-name"
          className="[&>div>input]:h-8"
          placeholder={placeholder}
        />
      }
      editMode={state.isEditing}
      disabled={hasOperationsDisabled}
      className="min-w-0"
      display={
        <H3
          className={documentTitleClasses({
            isDisabled: hasOperationsDisabled,
            isPlaceholder: !data?.name,
          })}
        >
          {data?.name || placeholder}
        </H3>
      }
      onClick={onClick}
      onCancel={onCancel}
      onConfirm={onUpdate}
      confirmDisabled={!state.value?.trim()}
      tooltipContent={data?.name || placeholder}
      withEditIcon={false}
    />
  );
};
